import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import type { Stage } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { ProgressRing } from '../../ui/progress-ring/progress-ring';
import { DealStageBadge } from './deal-stage-badge';

export const getStageOptions = ({
  className,
}: {
  className: string;
}): Option<Stage>[] => {
  return [
    {
      value: 'none',
      label: 'No Contract Status',
      icon: (
        <ProgressRing
          percentage={0}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '0',
    },
    {
      value: 'pre_contracting',
      label: 'Pre-Contracting',
      icon: (
        <ProgressRing
          percentage={20}
          className={className}
          circleClassName="text-purple-500"
        />
      ),
      hotkey: '1',
    },
    {
      value: 'finance_call',
      label: 'Finance Call',
      icon: (
        <ProgressRing
          percentage={40}
          className={className}
          circleClassName="text-purple-500"
        />
      ),
      hotkey: '2',
    },
    {
      value: 'contracting',
      label: 'Contracting',
      icon: (
        <ProgressRing
          percentage={60}
          className={className}
          circleClassName="text-blue-500"
        />
      ),
      hotkey: '3',
    },
    {
      value: 'funding',
      label: 'Funding',
      icon: (
        <ProgressRing
          percentage={80}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '4',
    },
    {
      value: 'funded',
      label: 'Funded',
      icon: (
        <ProgressRing
          percentage={100}
          className={className}
          circleClassName="text-green-500"
        />
      ),
      hotkey: '5',
    },
  ];
};

export const STAGE_OPTIONS_MAP = getStageOptions({ className: 'h-3 w-3' });

export type DealStageSelectorProps = {
  id: string;
  shortId: string;
  value: Deal['stage'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  useAbbreviation?: boolean;
  className?: string;
};

export function DealStageSelector({
  id,
  shortId,
  value,
  hideCopy = false,
  side = 'left',
  // DEV UX NOTE: do not turn `withHotkeyListener` in any list or board views
  // because it will lag the page. This is purely meant for limited number of
  // DealStageSelectors on a page.
  withHotkeyListener = false,
  className,
}: DealStageSelectorProps) {
  const queryClient = useQueryClient();

  const stageButtonRef = useRef<HTMLButtonElement>(null);

  const updateStageMutation = useMutation({
    onMutate: async ({ stage }: { stage: Deal['stage'] }) => {
      dealStore.updateStage({ id, stage });
    },
    mutationFn: async ({ stage }: { stage: Deal['stage'] }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/stage`, { stage })
        .catch((e) => {
          console.error('Error updating stage', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating stage', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  const handleStageChange = (newStage: Deal['stage']) => {
    updateStageMutation.mutate({
      stage: newStage,
    });
  };

  const currentOption = useMemo(() => {
    return STAGE_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.DEAL_CONTRACT_STATUS}</KeyboardKeyIcon>
        }
        placeholder="Set contract status..."
        currentOption={currentOption}
        onChange={handleStageChange}
        emptyMessage="No contract status found"
        options={STAGE_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = STAGE_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleStageChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div
        // DEV NOTE: without this div, combobox-popover won't work
        // that is, clicks will not open the combobox
        // I think the reason is children node needs to be able to receive a ref
        // in order to detect a click event, which makes it open
        // thus so this div is a workaround
        >
          <DealStageBadge
            value={value}
            hideCopy={hideCopy}
            buttonRef={stageButtonRef}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.DEAL_CONTRACT_STATUS}
          onHotkeyPressed={() => {
            stageButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
